/**
 * AudioManager - Core Singleton Audio System for Iron Meridian
 * 
 * Manages all audio playback using Web Audio API.
 * Handles volume control, debouncing, ducking, and graceful degradation.
 * 
 * UI/Event sounds are procedurally generated.
 * Music/Ambient use CC0/Public Domain audio files when available,
 * with procedural fallback if files are not present.
 */

import {
  DEFAULT_VOLUMES,
  AUDIO_CATEGORIES,
  AUDIO_TIMING,
  PROCEDURAL_PARAMS,
  loadAudioSettings,
  saveAudioSettings,
} from './audioConfig.js';

import { createClickSound } from './procedural/click.js';
import { createHoverSound } from './procedural/hover.js';
import { createConfirmSound, createCancelSound } from './procedural/confirm.js';
import { createAlertSound } from './procedural/alert.js';
import { createEventSound } from './procedural/events.js';

// File-based audio loader
import { 
  audioLoader, 
  createLoopingSource, 
  MUSIC_TRACKS, 
  AMBIENT_TRACKS 
} from './audioFileLoader.js';

// Procedural fallbacks (used when audio files aren't available)
import { createWarRoomAmbient, createTensionAmbient } from './ambient/warRoom.js';
import { createStrategicMusic } from './music/strategicAmbient.js';

// =====================================================================
// SINGLETON INSTANCE
// =====================================================================

let instance = null;

class AudioManager {
  constructor() {
    if (instance) {
      return instance;
    }
    instance = this;

    // Audio context (created on first user interaction)
    this.context = null;
    this.initialized = false;

    // Volume settings
    this.settings = loadAudioSettings();

    // Gain nodes for each category
    this.gainNodes = {
      master: null,
      ui: null,
      events: null,
      ambient: null,
      music: null,
    };

    // Active ambient/music sources
    this.activeSources = {
      ambient: null,
      music: null,
    };

    // Debounce tracking
    this.lastPlayTime = {};

    // Duck state
    this.isDucked = false;
    this.duckTimeout = null;

    // Audio buffer cache for loaded files
    this.bufferCache = new Map();

    // Bind methods for event listeners
    this.initialize = this.initialize.bind(this);
  }

  // ===================================================================
  // INITIALIZATION
  // ===================================================================

  /**
   * Initialize AudioContext on first user interaction.
   * Must be called from a user gesture (click, keypress, etc.)
   */
  async initialize() {
    if (this.initialized) {
      return true;
    }

    try {
      // Create AudioContext with fallback
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        console.warn('[AudioManager] Web Audio API not supported');
        return false;
      }

      this.context = new AudioContext();

      // Resume context if suspended (browser autoplay policy)
      if (this.context.state === 'suspended') {
        await this.context.resume();
      }

      // Create gain node hierarchy
      this._createGainNodes();

      this.initialized = true;
      console.log('[AudioManager] Initialized successfully');
      return true;
    } catch (error) {
      console.error('[AudioManager] Initialization failed:', error);
      return false;
    }
  }

  /**
   * Create the gain node hierarchy for volume control
   */
  _createGainNodes() {
    const ctx = this.context;

    // Master gain (connects to destination)
    this.gainNodes.master = ctx.createGain();
    this.gainNodes.master.connect(ctx.destination);
    this.gainNodes.master.gain.value = this.settings.muted ? 0 : this.settings.master;

    // Category gains (connect to master)
    for (const category of Object.values(AUDIO_CATEGORIES)) {
      this.gainNodes[category] = ctx.createGain();
      this.gainNodes[category].connect(this.gainNodes.master);
      this.gainNodes[category].gain.value = this.settings[category] ?? DEFAULT_VOLUMES[category];
    }
  }

  // ===================================================================
  // VOLUME CONTROL
  // ===================================================================

  /**
   * Set volume for a category
   * @param {string} category - 'master', 'ui', 'events', 'ambient', 'music'
   * @param {number} value - 0.0 to 1.0
   */
  setVolume(category, value) {
    const clampedValue = Math.max(0, Math.min(1, value));
    this.settings[category] = clampedValue;

    if (this.gainNodes[category]) {
      // Smooth transition
      this.gainNodes[category].gain.setTargetAtTime(
        clampedValue,
        this.context.currentTime,
        0.1
      );
    }

    saveAudioSettings(this.settings);
  }

  /**
   * Get volume for a category
   */
  getVolume(category) {
    return this.settings[category] ?? DEFAULT_VOLUMES[category];
  }

  /**
   * Mute all audio
   */
  muteAll() {
    this.settings.muted = true;
    if (this.gainNodes.master) {
      this.gainNodes.master.gain.setTargetAtTime(0, this.context.currentTime, 0.05);
    }
    saveAudioSettings(this.settings);
  }

  /**
   * Unmute all audio
   */
  unmuteAll() {
    this.settings.muted = false;
    if (this.gainNodes.master) {
      this.gainNodes.master.gain.setTargetAtTime(
        this.settings.master,
        this.context.currentTime,
        0.05
      );
    }
    saveAudioSettings(this.settings);
  }

  /**
   * Toggle mute state
   */
  toggleMute() {
    if (this.settings.muted) {
      this.unmuteAll();
    } else {
      this.muteAll();
    }
    return this.settings.muted;
  }

  /**
   * Check if audio is muted
   */
  isMuted() {
    return this.settings.muted;
  }

  // ===================================================================
  // UI SOUNDS (Procedural)
  // ===================================================================

  /**
   * Play a UI sound
   * @param {string} name - Sound name from UI_SOUNDS
   */
  playUI(name) {
    if (!this._canPlay(name)) return;

    switch (name) {
      case 'click':
        createClickSound(this.context, this.gainNodes.ui);
        break;
      case 'hover':
        createHoverSound(this.context, this.gainNodes.ui);
        break;
      case 'confirm':
        createConfirmSound(this.context, this.gainNodes.ui);
        break;
      case 'cancel':
        createCancelSound(this.context, this.gainNodes.ui);
        break;
      case 'alert':
        createAlertSound(this.context, this.gainNodes.ui);
        break;
      default:
        console.warn(`[AudioManager] Unknown UI sound: ${name}`);
    }

    this._recordPlayTime(name);
  }

  // ===================================================================
  // GAME EVENT SOUNDS
  // ===================================================================

  /**
   * Play a game event sound
   * @param {string} name - Sound name from EVENT_SOUNDS
   */
  playEvent(name) {
    if (!this._canPlay(name)) return;

    // Duck ambient/music during events
    this._duckAmbient();

    // Play the event sound
    createEventSound(this.context, this.gainNodes.events, name);

    this._recordPlayTime(name);
  }

  // ===================================================================
  // AMBIENT SOUNDS
  // ===================================================================

  /**
   * Play an ambient sound loop
   * Tries to load audio file first, falls back to procedural if not available
   * @param {string} name - Ambient sound name
   */
  async playAmbient(name) {
    console.log(`[AudioManager] playAmbient(${name}) - initialized:${this.initialized}, muted:${this.settings.muted}`);
    
    if (!this.initialized) {
      console.warn('[AudioManager] Cannot play ambient - not initialized. Click anywhere first.');
      return;
    }
    if (this.settings.muted) {
      console.log('[AudioManager] Ambient muted, skipping');
      return;
    }

    // Stop current ambient
    this.stopAmbient();

    // Try to load audio file first
    const trackUrl = AMBIENT_TRACKS[name];
    if (trackUrl) {
      try {
        const buffer = await audioLoader.loadAudio(this.context, trackUrl);
        if (buffer) {
          this.activeSources.ambient = createLoopingSource(
            this.context, 
            buffer, 
            this.gainNodes.ambient,
            { fadeInDuration: 2, fadeOutDuration: 2, volume: 0.8 }
          );
          console.log(`[AudioManager] Ambient '${name}' started (file-based)`);
          return;
        }
      } catch (error) {
        console.log(`[AudioManager] File not found for ambient '${name}', using procedural fallback`);
      }
    }

    // Fallback to procedural ambient
    switch (name) {
      case 'warRoom':
        this.activeSources.ambient = createWarRoomAmbient(this.context, this.gainNodes.ambient);
        console.log('[AudioManager] War room ambient started (procedural fallback)');
        break;
      case 'tension':
        this.activeSources.ambient = createTensionAmbient(this.context, this.gainNodes.ambient);
        console.log('[AudioManager] Tension ambient started (procedural fallback)');
        break;
      default:
        console.warn(`[AudioManager] Unknown ambient sound: ${name}`);
    }
  }

  /**
   * Stop ambient sound
   */
  stopAmbient() {
    if (this.activeSources.ambient) {
      this.activeSources.ambient.stop();
      this.activeSources.ambient = null;
    }
  }

  // ===================================================================
  // MUSIC - File-based with procedural fallback
  // ===================================================================

  /**
   * Play music track
   * Tries to load audio file first, falls back to procedural if not available
   * @param {string} name - Music track name ('strategic', 'tension', etc.)
   */
  async playMusic(name) {
    console.log(`[AudioManager] playMusic(${name}) - initialized:${this.initialized}, muted:${this.settings.muted}`);
    
    if (!this.initialized) {
      console.warn('[AudioManager] Cannot play music - not initialized');
      return;
    }
    if (this.settings.muted) {
      console.log('[AudioManager] Music muted, skipping');
      return;
    }

    // Stop current music
    this.stopMusic();

    // Try to load audio file first
    const trackUrl = MUSIC_TRACKS[name];
    if (trackUrl) {
      try {
        const buffer = await audioLoader.loadAudio(this.context, trackUrl);
        if (buffer) {
          this.activeSources.music = createLoopingSource(
            this.context, 
            buffer, 
            this.gainNodes.music,
            { fadeInDuration: 3, fadeOutDuration: 2, volume: 0.6 }
          );
          console.log(`[AudioManager] Music '${name}' started (file-based)`);
          return;
        }
      } catch (error) {
        console.log(`[AudioManager] File not found for music '${name}', using procedural fallback`);
      }
    }

    // Fallback to procedural music
    switch (name) {
      case 'strategic':
        this.activeSources.music = createStrategicMusic(this.context, this.gainNodes.music);
        console.log('[AudioManager] Strategic music started (procedural fallback)');
        break;
      default:
        console.warn(`[AudioManager] Unknown music track: ${name}`);
    }
  }

  /**
   * Stop music
   */
  stopMusic() {
    if (this.activeSources.music) {
      this.activeSources.music.stop();
      this.activeSources.music = null;
    }
  }

  // ===================================================================
  // STOP CONTROLS
  // ===================================================================

  /**
   * Stop sounds in a category
   * @param {string} category - Category to stop
   */
  stop(category) {
    switch (category) {
      case AUDIO_CATEGORIES.AMBIENT:
        this.stopAmbient();
        break;
      case AUDIO_CATEGORIES.MUSIC:
        this.stopMusic();
        break;
      // UI and Events are short, no need to stop
    }
  }

  /**
   * Stop all audio
   */
  stopAll() {
    this.stopAmbient();
    this.stopMusic();
  }

  // ===================================================================
  // AUDIO DUCKING
  // ===================================================================

  /**
   * Temporarily reduce ambient/music volume during events
   */
  _duckAmbient() {
    if (this.isDucked) {
      // Reset timeout if already ducked
      clearTimeout(this.duckTimeout);
    } else {
      this.isDucked = true;
      const duckLevel = AUDIO_TIMING.DUCK_LEVEL;

      if (this.gainNodes.ambient) {
        this.gainNodes.ambient.gain.setTargetAtTime(
          this.settings.ambient * duckLevel,
          this.context.currentTime,
          0.05
        );
      }
      if (this.gainNodes.music) {
        this.gainNodes.music.gain.setTargetAtTime(
          this.settings.music * duckLevel,
          this.context.currentTime,
          0.05
        );
      }
    }

    // Restore after duration
    this.duckTimeout = setTimeout(() => {
      this._unduckAmbient();
    }, AUDIO_TIMING.DUCK_DURATION_MS);
  }

  /**
   * Restore ambient/music volume after ducking
   */
  _unduckAmbient() {
    this.isDucked = false;
    if (this.gainNodes.ambient) {
      this.gainNodes.ambient.gain.setTargetAtTime(
        this.settings.ambient,
        this.context.currentTime,
        0.2
      );
    }
    if (this.gainNodes.music) {
      this.gainNodes.music.gain.setTargetAtTime(
        this.settings.music,
        this.context.currentTime,
        0.2
      );
    }
  }

  // ===================================================================
  // DEBOUNCE & PLAYBACK CONTROL
  // ===================================================================

  /**
   * Check if a sound can be played (initialized, not muted, not debounced)
   */
  _canPlay(soundName) {
    if (!this.initialized) {
      return false;
    }
    if (this.settings.muted) {
      return false;
    }

    // Check debounce
    const now = performance.now();
    const lastTime = this.lastPlayTime[soundName] || 0;
    if (now - lastTime < AUDIO_TIMING.MIN_REPEAT_INTERVAL_MS) {
      return false;
    }

    return true;
  }

  /**
   * Record when a sound was played for debouncing
   */
  _recordPlayTime(soundName) {
    this.lastPlayTime[soundName] = performance.now();
  }

  // ===================================================================
  // UTILITY
  // ===================================================================

  /**
   * Get all current settings
   */
  getSettings() {
    return { ...this.settings };
  }

  /**
   * Check if audio is available
   */
  isAvailable() {
    return !!(window.AudioContext || window.webkitAudioContext);
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopAll();
    if (this.context) {
      this.context.close();
      this.context = null;
    }
    this.initialized = false;
    instance = null;
  }
}

// =====================================================================
// EXPORT SINGLETON
// =====================================================================

export const audioManager = new AudioManager();
export default audioManager;
